import { configureStore } from "@reduxjs/toolkit";
import { type InitialisedFirebase } from "igfd-commons";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistStore,
} from "redux-persist";

import type { AxiosInstances } from "../context";
import { type ThunkExtraArgument } from "../types/thunk";
import { FirestoreBackedBrandingApi } from "./backendApis/brandingApi";
import { FirestoreBackedDishApi } from "./backendApis/dishApi";
import { FirestoreBackedLocationApi } from "./backendApis/locationApi";
import { FirestoreBackedMenuApi } from "./backendApis/menuApi";
import { FirestoreBackedProductApi } from "./backendApis/productApi";
import { ResolvedEdiblesBackend } from "./backendApis/resolvedEdiblesApi";
import { FirestoreBackedRestaurantApi } from "./backendApis/restaurantApi";
import { FirestoreBackedSupplierApi } from "./backendApis/supplierApi";
import { FirestoreBackedUserApi } from "./backendApis/userApi";
import { rootReducer } from "./reducers/rootReducer";

const reduxPersistActions = [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER];

export function storeWithThunkExtraArgs(
  initialisedFirebase: InitialisedFirebase,
  axiosInstances: AxiosInstances
) {
  const { firestoreRefs } = initialisedFirebase;
  const menuApi = new FirestoreBackedMenuApi(firestoreRefs);
  const dishApi = new FirestoreBackedDishApi({
    firestoreRefs,
    // create a new closure here to pass the complete MenuApi as an object to the
    // callback from the dish Api.
    beforeDeleteDishFromMenuSection: (transaction, restaurantUuid, dishId) =>
      menuApi.deleteDishFromMenuSections(transaction, restaurantUuid, dishId),
  });
  const productApi = new FirestoreBackedProductApi({ firestoreRefs });
  const resolvedEdiblesApi = new ResolvedEdiblesBackend({
    axiosClientWithoutAuth: axiosInstances.clientWithoutAuth,
  });
  const restaurantApi = new FirestoreBackedRestaurantApi(firestoreRefs);
  const locationApi = new FirestoreBackedLocationApi(firestoreRefs);
  const brandingApi = new FirestoreBackedBrandingApi(firestoreRefs);
  const supplierApi = new FirestoreBackedSupplierApi({ firestoreRefs });
  const userApi = new FirestoreBackedUserApi(firestoreRefs);

  const store = configureStore({
    reducer: rootReducer({ usePersist: true }),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Actions from redux persist are not serializable.
          // However we do want to make sure our own actions are serializable.
          ignoredActions: reduxPersistActions,
        },
        thunk: {
          extraArgument: {
            dishApi,
            productApi,
            resolvedEdiblesApi,
            menuApi,
            restaurantApi,
            locationApi,
            brandingApi,
            supplierApi,
            userApi,
            axiosInstances,
          } as ThunkExtraArgument,
        },
      }),
  });

  const persistor = persistStore(store);

  return { store, persistor };
}
